import { Component, inject, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { NzIconModule } from "ng-zorro-antd/icon";
import { CommonModule } from "@angular/common";
import { takeUntil } from "rxjs/operators";
import { Subject, combineLatest } from "rxjs";
import { KYCStatus } from "@/app/core/entities/business.entity";
import { selectorCurrentUser } from "@/app/stores/auth/auth.selectors";
import { loadBeneficiary } from "@/app/stores/wallets/wallets.actions";
import { selectorBeneficiary, selectorBeneficiaryStatus } from "@/app/stores/wallets/wallets.selectors";
import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { StoreWalletsModule } from "@/app/stores/wallets/store-wallets.module";
import { PermissionChecker } from "@/app/core/services/permission-checker.service";
import { Role } from "@/app/core/dtos/user.dto";
import { Helper } from "@/app/core/utils/helper";

export type BannerType = "kyb-verification" | "kyb-in-review" | "kyb-completion" | null;

@Component({
  selector: "app-kyb-banner",
  imports: [CommonModule, NzIconModule, StoreWalletsModule],
  templateUrl: "./kyb-banner.component.html",
})
export class KybBannerComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly permissionChecker = inject(PermissionChecker);

  get businessId(): string | null {
    // Try to get businessId from current route or parent route
    return this.route.snapshot.params["businessId"] || this.route.snapshot.parent?.params["businessId"] || null;
  }
  private destroy$ = new Subject<void>();

  bannerType: BannerType = null;
  bannerConfig: {
    icon: string;
    text: string;
    buttonText: string;
    buttonAction: () => void;
    bgColor: string;
    borderColor: string;
    iconColor: string;
  } | null = null;

  ngOnInit() {
    // Subscribe to user, beneficiary, and role to determine banner type
    combineLatest([this.store.select(selectorCurrentUser), this.store.select(selectorBeneficiary), this.store.select(selectorBeneficiaryStatus), this.permissionChecker.currentRole$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, beneficiary, beneficiaryStatus, currentRole]) => {
        // Hide banner for Project Managers
        if (currentRole === Role.PROJECT_MANAGER) {
          this.bannerType = null;
          this.bannerConfig = null;
          return;
        }

        if (!user || !user.businessUsers?.length || !this.businessId) {
          this.bannerType = null;
          this.bannerConfig = null;
          return;
        }

        const business = user.businessUsers[0]?.business;

        // Show KYB In Review banner when submission is pending review
        if (business && business.kycStatus === KYCStatus.IN_REVIEW && this.businessId) {
          this.bannerType = "kyb-in-review";
          this.bannerConfig = {
            icon: "clock-circle",
            text: "Your KYB submission is under review. We'll notify you once verification is complete — usually within 24 hours.",
            buttonText: "",
            bgColor: "#4E36F5",
            borderColor: "#4E36F5",
            iconColor: "#ffffff",
            buttonAction: () => {},
          };
          return;
        }

        // Show KYB Required banner for all other non-ACTIVE statuses
        if (business && business.kycStatus !== KYCStatus.ACTIVE && this.businessId) {
          this.bannerType = "kyb-verification";
          this.bannerConfig = {
            icon: "warning",
            text: "Complete KYB to create your first project — Business verification is required before clients can fund escrow.",
            buttonText: "Verify Now >",
            bgColor: "#4E36F5",
            borderColor: "#4E36F5",
            iconColor: "#ffffff",
            buttonAction: () => {
              this.router.navigate(["/", this.businessId, "verify-business"]);
            },
          };
          return;
        }

        // Only show the completion/beneficiary banner once KYB is ACTIVE.
        if (business && business.kycStatus === KYCStatus.ACTIVE && this.businessId) {
          // Load beneficiary if not already loaded
          if (beneficiaryStatus === ELoadingStatus.idle) {
            this.store.dispatch(loadBeneficiary({ businessId: this.businessId }));
          }

          // Show completion banner if beneficiary is loaded and doesn't exist
          if (beneficiaryStatus === ELoadingStatus.loaded && !beneficiary?.payoutBeneficiaryId) {
            this.bannerType = "kyb-completion";
            this.bannerConfig = {
              icon: "info-circle",
              text: "Congratulations! Your KYB is completed. We suggest that you add a beneficiary bank account in the wallets tab as it can take up to 24 hours to approve",
              buttonText: "Create Beneficiary",
              bgColor: "#F0F4FF",
              borderColor: "#D6E4FF",
              iconColor: "#667085",
              buttonAction: () => {
                if (this.businessId && !Helper.isMobileDevice()) {
                  this.router.navigate([`/${this.businessId}/wallets`], { queryParams: { tab: "beneficiary" } });
                }
              },
            };
            return;
          }
        }

        // No banner needed
        this.bannerType = null;
        this.bannerConfig = null;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
